/**
 * MAV Hierarchy Feedback API
 *
 * Automatically notifies MAV when hierarchy changes occur in OWL,
 * enabling MAV to stay synchronized with the latest entity data.
 */

import Vue from 'vue';
import MAV from '@/api/mav';

function hasReviewPermission() {
  const auth = Vue.prototype.$auth;
  return auth && typeof auth.hasPermissions === 'function' && auth.hasPermissions('owl.review');
}

/**
 * Send hierarchy feedback to MAV
 * @param {number} owlId - The tree ID (parent/brand/channel ID)
 * @param {string} type - Entity type: 'parent', 'brand', or 'channel'
 * @returns {Promise} - Resolves when feedback is sent
 */
export async function sendHierarchyFeedback(owlId, type) {
  if (!hasReviewPermission()) {
    console.log('[MAV Hierarchy Feedback] Skipped: user lacks owl.review permission');
    return Promise.resolve();
  }

  if (!owlId || !type) {
    console.warn('[MAV Hierarchy Feedback] Missing required parameters:', { owlId, type });
    return Promise.resolve();
  }

  const validTypes = ['parent', 'brand', 'channel'];
  if (!validTypes.includes(type)) {
    console.warn('[MAV Hierarchy Feedback] Invalid type:', type);
    return Promise.resolve();
  }

  try {
    const payload = {
      owl_id: owlId,
      type: type,
    };

    console.log('[MAV Hierarchy Feedback] Sending:', payload);

    const response = await MAV.post('/hierarchy/import', payload);

    console.log('[MAV Hierarchy Feedback] Success:', response.data);
    return response.data;
  } catch (error) {
    // Non-blocking error - log but don't throw
    console.error('[MAV Hierarchy Feedback] Error:', error.message || error);
    return Promise.resolve();
  }
}

/**
 * Send feedback for parent creation/edit
 * @param {number} parentId - Parent ID
 */
export function notifyParentChange(parentId) {
  return sendHierarchyFeedback(parentId, 'parent');
}

/**
 * Send feedback for brand creation/edit
 * @param {number} brandId - Brand ID
 */
export function notifyBrandChange(brandId) {
  return sendHierarchyFeedback(brandId, 'brand');
}

/**
 * Send feedback for channel creation/edit
 * @param {number} channelId - Channel ID
 */
export function notifyChannelChange(channelId) {
  return sendHierarchyFeedback(channelId, 'channel');
}

/**
 * Send feedback for transfer operations
 * For transfers, we notify about the parent entity that now contains the transferred child
 *
 * @param {number} targetId - The target tree ID where the entity was transferred
 * @param {string} targetType - Type of the target entity
 */
export function notifyTransfer(targetId, targetType) {
  return sendHierarchyFeedback(targetId, targetType);
}

/**
 * Determine entity type from tree ID by checking the response structure
 * This is a helper for cases where we have a tree_id but don't know the type
 *
 * @param {Object} entityData - Entity data from API response
 * @returns {string|null} - 'parent', 'brand', 'channel', or null
 */
export function detectEntityType(entityData) {
  if (!entityData) return null;

  // Check for channel indicators
  if (entityData.brand_id !== undefined || entityData.channel_id !== undefined) {
    return 'channel';
  }

  // Check for brand indicators
  if (entityData.parent_id !== undefined && entityData.brand_id === undefined) {
    return 'brand';
  }

  // Check for parent indicators
  if (entityData.parent_id === undefined && entityData.brand_id === undefined) {
    return 'parent';
  }

  return null;
}

export default {
  sendHierarchyFeedback,
  notifyParentChange,
  notifyBrandChange,
  notifyChannelChange,
  notifyTransfer,
  detectEntityType,
};
